/**
 * JoinPage — Team application form with EmailJS submission.
 */
import React, { useState } from 'react';
import PageHeader from './roar/PageHeader';
import styles from './JoinPage.module.css';

const INTEREST_OPTIONS = [
  'Mechanical Design',
  'Programming/Software',
  'Electrical/Electronics',
  'CAD Design',
  'Project Management',
  'Marketing/Outreach',
  'Competition Strategy',
  'Workshop Safety',
];

const AVAILABILITY_OPTIONS = [
  'Monday Afternoons',
  'Tuesday Afternoons',
  'Wednesday Afternoons',
  'Thursday Afternoons',
  'Friday Afternoons',
  'Saturday Mornings',
  'Summer Programs',
  'Competition Weekends',
];

const INITIAL_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  grade: '',
  school: '',
  experience: '',
  interests: [],
  availability: [],
  whyJoin: '',
  parentName: '',
  parentEmail: '',
  parentPhone: '',
  emergencyContact: '',
  emergencyPhone: '',
  medicalInfo: '',
  agreeToTerms: false,
  photoConsent: false,
};

function JoinPage() {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      if (name === 'interests' || name === 'availability') {
        setFormData((prev) => ({
          ...prev,
          [name]: checked
            ? [...prev[name], value]
            : prev[name].filter((item) => item !== value),
        }));
      } else {
        setFormData((prev) => ({ ...prev, [name]: checked }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const sendEmail = (emailData) => {
    const emailjsService = 'service_zuqxl8u';
    const emailjsTemplate = 'template_o71wisf';
    const emailjsUser = '469kDbVscgrGih7Sy';

    const doSend = () => {
      window.emailjs
        .send(emailjsService, emailjsTemplate, emailData)
        .then(() => {
          setSubmitted(true);
          setIsSubmitting(false);
        })
        .catch((error) => {
          console.error('EmailJS error:', error);
          setFormError('There was an error submitting your application. Please try again or email ffxrobotics@gmail.com.');
          setIsSubmitting(false);
        });
    };

    if (typeof window.emailjs === 'undefined') {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/@emailjs/browser@3/dist/email.min.js';
      script.onload = () => {
        window.emailjs.init(emailjsUser);
        doSend();
      };
      document.head.appendChild(script);
    } else {
      doSend();
    }
  };

  const submitApplication = async () => {
    setFormError('');

    if (!formData.agreeToTerms) {
      setFormError('Please agree to the team guidelines and safety protocols.');
      return;
    }

    if (!formData.firstName || !formData.lastName || !formData.email || !formData.whyJoin) {
      setFormError('Please fill in all required fields (name, email, and why you want to join).');
      return;
    }

    setIsSubmitting(true);

    const emailData = {
      to_email: 'ffxrobotics@gmail.com',
      from_name: `${formData.firstName} ${formData.lastName}`,
      student_name: `${formData.firstName} ${formData.lastName}`,
      student_email: formData.email,
      student_phone: formData.phone,
      grade: formData.grade,
      school: formData.school,
      experience: formData.experience,
      interests: formData.interests.join(', '),
      availability: formData.availability.join(', '),
      why_join: formData.whyJoin,
      parent_name: formData.parentName,
      parent_email: formData.parentEmail,
      parent_phone: formData.parentPhone,
      emergency_contact: formData.emergencyContact,
      emergency_phone: formData.emergencyPhone,
      medical_info: formData.medicalInfo || 'None specified',
      photo_consent: formData.photoConsent ? 'Yes' : 'No',
      submission_date: new Date().toLocaleDateString(),
    };

    try {
      sendEmail(emailData);
    } catch (error) {
      console.error('Error sending email:', error);
      setFormError('There was an error submitting your application. Please try again.');
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM);
    setSubmitted(false);
    setFormError('');
  };

  if (submitted) {
    return (
      <div className={`roar-page ${styles.successWrap}`}>
        <div className={styles.successPanel}>
          <h1 className={styles.successTitle}>Application submitted</h1>
          <p className={styles.successText}>
            Thanks for applying. We&apos;ve received your application and will review it shortly.
          </p>
          <p className={styles.successText}>
            Expect to hear back within 3–5 business days with next steps.
          </p>
          <button
            type="button"
            className={`roar-btn roar-btn-primary ${styles.successBtn}`}
            onClick={resetForm}
          >
            Submit another application
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`roar-page ${styles.page}`}>
      <PageHeader
        eyebrow="Join"
        title="Join the team"
        lead="New members are welcome — no prior robotics experience required. Fill out the application and we'll follow up to schedule a meet-and-greet."
      />

      <div className={styles.wrap}>
        <div className={styles.formPanel}>
          {formError && (
            <p className={styles.formError} role="alert">{formError}</p>
          )}

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Personal information</h2>
            <div className={styles.grid}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="firstName">First name *</label>
                <input
                  id="firstName"
                  className={styles.input}
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="lastName">Last name *</label>
                <input
                  id="lastName"
                  className={styles.input}
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
            <div className={styles.grid}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="email">Email *</label>
                <input
                  id="email"
                  className={styles.input}
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="phone">Phone</label>
                <input
                  id="phone"
                  className={styles.input}
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <div className={styles.grid}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="grade">Current grade</label>
                <select
                  id="grade"
                  className={styles.select}
                  name="grade"
                  value={formData.grade}
                  onChange={handleInputChange}
                >
                  <option value="">Select grade</option>
                  <option value="9">9th grade</option>
                  <option value="10">10th grade</option>
                  <option value="11">11th grade</option>
                  <option value="12">12th grade</option>
                </select>
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="school">School</label>
                <input
                  id="school"
                  className={styles.input}
                  type="text"
                  name="school"
                  value={formData.school}
                  onChange={handleInputChange}
                  placeholder="Fairfax High School"
                />
              </div>
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Experience & interests</h2>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="experience">Previous robotics / STEM experience</label>
              <textarea
                id="experience"
                className={styles.textarea}
                name="experience"
                value={formData.experience}
                onChange={handleInputChange}
                rows={3}
                placeholder="Any robotics, programming, or engineering background — or leave blank if you're new."
              />
            </div>
            <div className={styles.field}>
              <span className={styles.label}>Areas of interest</span>
              <div className={styles.checkboxGrid}>
                {INTEREST_OPTIONS.map((interest) => (
                  <label key={interest} className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      name="interests"
                      value={interest}
                      checked={formData.interests.includes(interest)}
                      onChange={handleInputChange}
                    />
                    {interest}
                  </label>
                ))}
              </div>
            </div>
            <div className={styles.field}>
              <span className={styles.label}>Availability</span>
              <div className={styles.checkboxGrid}>
                {AVAILABILITY_OPTIONS.map((time) => (
                  <label key={time} className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      name="availability"
                      value={time}
                      checked={formData.availability.includes(time)}
                      onChange={handleInputChange}
                    />
                    {time}
                  </label>
                ))}
              </div>
            </div>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="whyJoin">Why do you want to join? *</label>
              <textarea
                id="whyJoin"
                className={`${styles.textarea} ${styles.textareaTall}`}
                name="whyJoin"
                value={formData.whyJoin}
                onChange={handleInputChange}
                rows={4}
                required
                placeholder="What you hope to learn or contribute this season."
              />
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Parent / guardian</h2>
            <div className={styles.grid}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="parentName">Parent / guardian name</label>
                <input
                  id="parentName"
                  className={styles.input}
                  type="text"
                  name="parentName"
                  value={formData.parentName}
                  onChange={handleInputChange}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="parentPhone">Parent phone</label>
                <input
                  id="parentPhone"
                  className={styles.input}
                  type="tel"
                  name="parentPhone"
                  value={formData.parentPhone}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <div className={styles.gridSingle}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="parentEmail">Parent email</label>
                <input
                  id="parentEmail"
                  className={styles.input}
                  type="email"
                  name="parentEmail"
                  value={formData.parentEmail}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <div className={styles.grid}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="emergencyContact">Emergency contact name</label>
                <input
                  id="emergencyContact"
                  className={styles.input}
                  type="text"
                  name="emergencyContact"
                  value={formData.emergencyContact}
                  onChange={handleInputChange}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="emergencyPhone">Emergency phone</label>
                <input
                  id="emergencyPhone"
                  className={styles.input}
                  type="tel"
                  name="emergencyPhone"
                  value={formData.emergencyPhone}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="medicalInfo">Medical info / allergies</label>
              <textarea
                id="medicalInfo"
                className={styles.textarea}
                name="medicalInfo"
                value={formData.medicalInfo}
                onChange={handleInputChange}
                rows={2}
                placeholder="Optional — anything we should know for safety."
              />
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Agreements</h2>
            <div className={styles.field}>
              <label className={styles.consentLabel}>
                <input
                  type="checkbox"
                  name="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onChange={handleInputChange}
                />
                <span>
                  I agree to team guidelines and safety protocols, and understand that
                  robotics activities involve inherent risks. I commit to attending meetings
                  and competitions as scheduled. *
                </span>
              </label>
            </div>
            <div className={styles.field}>
              <label className={styles.consentLabel}>
                <input
                  type="checkbox"
                  name="photoConsent"
                  checked={formData.photoConsent}
                  onChange={handleInputChange}
                />
                <span>
                  I consent to photos and videos taken during team activities for
                  promotional and educational use.
                </span>
              </label>
            </div>
          </section>

          <div className={styles.submitWrap}>
            <button
              type="button"
              className={styles.submit}
              onClick={submitApplication}
              disabled={!formData.agreeToTerms || isSubmitting}
            >
              {isSubmitting ? 'Submitting…' : 'Submit application'}
            </button>
            <p className={styles.submitNote}>* Required fields</p>
          </div>
        </div>

        <aside className={styles.infoBox}>
          <h3 className={styles.infoTitle}>What happens next</h3>
          <p className={styles.infoText}>
            Team leads review applications and reach out within 3–5 business days to
            schedule an informal meet-and-greet.
          </p>
        </aside>
      </div>
    </div>
  );
}

export default JoinPage;
